import React, { useState, useEffect } from "react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/sidebar";
import Header from "../../components/header";
import "../../assets/CSS/dashboard.css";
import "../../assets/CSS/forms.css";
import API_BASE_URL from "../../config";
import "../../assets/CSS/loader.css";

function DisposalEntry() {
  const navigate = useNavigate();
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loader, setLoader] = useState(false);
  const [projectQuery, setProjectQuery] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [timber, setTimber] = useState([]);
  const [props, setProps] = useState([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    fetch(API_BASE_URL + "/api/timber")
      .then((res) => res.json())
      .then((data) => setTimber(data.timbers || []))
      .catch((err) => console.error("Error fetching timber:", err));
  }, [reload]);

  useEffect(() => {
    fetch(API_BASE_URL + "/api/props")
      .then((res) => res.json())
      .then((data) => setProps(data.props || []))
      .catch((err) => console.error("Error fetching props:", err));
  }, [reload]);

  useEffect(() => {
    if (!projectQuery) {
      setProjectOptions([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetch(`${API_BASE_URL}/api/project-options?query=${projectQuery}`)
        .then((res) => res.json())
        .then((data) => {
          setProjectOptions(data.projects.map((d) => d.name));
        })
        .catch((err) => console.error("Error fetching:", err));
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [projectQuery]);

  const handleSubmit = async (e) => {
    setLoader(true);
    e.preventDefault();
    setIsSubmitting(true);

    if (!formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      setIsSubmitting(false);
      setLoader(false);
      return;
    }

    const formData = new FormData(formRef.current);

    // Convert timber
    const timberData = timber.map((item, index) => ({
      type: item.type,
      pcs: formData.get(`timber[${index}][pcs]`) || 0,
      vol: formData.get(`timber[${index}][vol]`) || 0,
    }));

    // Convert props
    const propsData = props.map((item, index) => ({
      type: item.type,
      pcs: formData.get(`props[${index}][pcs]`) || 0,
    }));

    const payload = {
      month: formData.get("month"),
      project: formData.get("project"),
      total: formData.get("total"),
      felled: formData.get("felled"),
      fw: formData.get("F/W") || 0,
      remarks: formData.get("remarks"),
      timber: timberData,
      props: propsData,
    };

    try {
      const response = await fetch(
        API_BASE_URL + "/api/project-progress/disposal-entry",
        {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert("Disposal added successfully");
        formRef.current.reset();
        setProjectQuery("");
        setProjectOptions([]);
        setIsSubmitting(false);
        setLoader(false);
      } else {
        alert(result.error || "Error uploading data");
        setIsSubmitting(false);
        setLoader(false);
      }
    } catch (error) {
      console.error(error);
      alert("Server error");
      setIsSubmitting(false);
      setLoader(false);
    }
  };

  return (
    <div>
      {loader && (
        <div className="loader">
          <div className="loader-spinner"></div>
        </div>
      )}
      <SideBar />
      <div className="main-content">
        <Header />
        <div id="form-content">
          <div id="form-header">
            <p>Disposal Entry</p>
          </div>
          <hr />
          <form id="form" ref={formRef}>
            <div id="form-input">
              <label htmlFor="month">
                Month<span className="required">*</span>
              </label>
              <input
                type="month"
                name="month"
                id="month"
                placeholder="Enter Month"
                required
              />
            </div>
            <div id="form-input">
              <label htmlFor="project">
                Project Name<span className="required">*</span>
              </label>
              <input
                type="text"
                name="project"
                id="project"
                placeholder="Select Project Name"
                list="project-options"
                value={projectQuery}
                onChange={(e) => setProjectQuery(e.target.value)}
                autoComplete="off"
                required
              />
              <datalist id="project-options">
                {projectOptions.map((option, index) => (
                  <option key={index} value={option} />
                ))}
              </datalist>
            </div>
            <div id="form-input">
              <label htmlFor="total">
                Total Trees<span className="required">*</span>
              </label>
              <input
                type="number"
                name="total"
                id="total"
                step="1"
                placeholder="Enter Total Number of Trees"
                required
              />
            </div>
            <div id="form-input">
              <label htmlFor="felled">
                Total Felled Trees<span className="required">*</span>
              </label>
              <input
                type="number"
                name="felled"
                id="felled"
                step="1"
                placeholder="Enter Total Number of Felled Trees"
                required
              />
            </div>
            <div id="form-input">
              <label htmlFor="F/W">F/W</label>
              <input
                type="number"
                name="F/W"
                id="F/W"
                step="0.01"
                min="0"
                defaultValue={0}
                placeholder="Enter F/W"
              />
            </div>
            <div id="form-input">
              <label htmlFor="remarks">Remarks</label>
              <input
                type="text"
                name="remarks"
                id="remarks"
                placeholder="Enter Remarks"
              />
            </div>
            <table>
              <thead>
                <tr>
                  <th colSpan="3" style={{ textAlign: "center" }}>
                    TIMBER
                  </th>
                </tr>
                <tr>
                  <th>Name</th>
                  <th>PCS</th>
                  <th>Vol M3</th>
                </tr>
              </thead>
              <tbody>
                {timber.map((item, index) => (
                  <tr key={item.id}>
                    <td>{item.type}</td>
                    <td>
                      <input
                        type="number"
                        name={`timber[${index}][pcs]`}
                        placeholder="Enter PCS"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        name={`timber[${index}][vol]`}
                        placeholder="Enter Volume (m3)"
                        step="0.01"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table id="props-forms">
              <thead>
                <tr>
                  <th colSpan="2" style={{ textAlign: "center" }}>
                    PROPS
                  </th>
                </tr>
                <tr>
                  <th>Name</th>
                  <th>PCS</th>
                </tr>
              </thead>
              <tbody>
                {props.map((item, index) => (
                  <tr key={item.id}>
                    <td>{item.type}</td>
                    <td>
                      <input
                        type="number"
                        name={`props[${index}][pcs]`}
                        placeholder="Enter PCS"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </form>
          <div className="button">
            <button
              type="button"
              className="btn"
              id="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              SUBMIT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DisposalEntry;
